/* ==========================================================================
   KPWA Luxury theme script
   No dependencies. Loaded with "defer" from layout/theme.liquid.

   1. Helpers            5. Product variants and gallery
   2. Header             6. Collection sorting
   3. Drawers            7. Reveal on scroll
   4. Cart               8. Recommendations and theme editor
   7b. Waitlist form and scroll link
   ========================================================================== */
(function () {
  'use strict';

  /* 1. Helpers
     ------------------------------------------------------------------------ */
  var KPWA = window.KPWA || {};
  var routes = KPWA.routes || {};
  var strings = KPWA.strings || {};

  function qs(selector, scope) {
    return (scope || document).querySelector(selector);
  }

  function qsa(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* Formats cents using the shop's own money format, e.g. "${{amount}}". */
  function formatMoney(cents) {
    var format = KPWA.moneyFormat || '${{amount}}';
    var value = Number(cents) || 0;

    function build(precision, thousands, decimal) {
      var parts = (value / 100).toFixed(precision).split('.');
      var whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      return whole + (parts[1] ? decimal + parts[1] : '');
    }

    return format.replace(/\{\{\s*(\w+)\s*\}\}/, function (match, key) {
      switch (key) {
        case 'amount_no_decimals':
          return build(0, ',', '.');
        case 'amount_with_comma_separator':
          return build(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return build(0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return build(2, "'", '.');
        case 'amount_no_decimals_with_space_separator':
          return build(0, ' ', ',');
        case 'amount_with_space_separator':
          return build(2, ' ', ',');
        case 'amount_with_period_and_space_separator':
          return build(2, ' ', '.');
        default:
          return build(2, ',', '.');
      }
    });
  }

  /* 2. Header
     ------------------------------------------------------------------------ */
  function initHeader() {
    var header = qs('[data-header]');
    if (!header) return;

    function setHeight() {
      document.documentElement.style.setProperty('--header-h', header.offsetHeight + 'px');
    }

    function onScroll() {
      header.classList.toggle('is-scrolled', window.scrollY > 24);
    }

    setHeight();
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    if ('ResizeObserver' in window) {
      new ResizeObserver(setHeight).observe(header);
    } else {
      window.addEventListener('resize', setHeight);
    }
  }

  /* 3. Drawers (mobile menu, search, cart)
     ------------------------------------------------------------------------ */
  var Drawers = {
    active: null,
    lastFocus: null,

    open: function (id, trigger) {
      var drawer = document.getElementById(id);
      if (!drawer) return false;
      if (this.active && this.active !== drawer) this.close(true);

      this.lastFocus = trigger || document.activeElement;
      this.active = drawer;
      drawer.classList.add('is-open');
      document.body.classList.add('has-open-drawer');
      qsa('[aria-controls="' + id + '"]').forEach(function (control) {
        control.setAttribute('aria-expanded', 'true');
      });

      var target = qs('[data-drawer-focus]', drawer) || qs('.drawer__close', drawer);
      if (target) {
        window.setTimeout(function () {
          target.focus({ preventScroll: true });
        }, 120);
      }
      return true;
    },

    close: function (keepFocus) {
      var drawer = this.active;
      if (!drawer) return;
      drawer.classList.remove('is-open');
      document.body.classList.remove('has-open-drawer');
      qsa('[aria-controls="' + drawer.id + '"]').forEach(function (control) {
        control.setAttribute('aria-expanded', 'false');
      });
      this.active = null;
      if (!keepFocus && this.lastFocus && document.contains(this.lastFocus)) {
        this.lastFocus.focus({ preventScroll: true });
      }
    },

    trapFocus: function (event) {
      if (!this.active) return;
      var focusable = qsa(
        'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])',
        this.active
      ).filter(function (el) {
        return el.offsetParent !== null;
      });
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  function initDrawers() {
    document.addEventListener('click', function (event) {
      var opener = event.target.closest('[data-drawer-open]');
      if (opener) {
        if (Drawers.open(opener.getAttribute('data-drawer-open'), opener)) event.preventDefault();
        return;
      }

      if (event.target.closest('[data-drawer-close]')) {
        event.preventDefault();
        Drawers.close();
        return;
      }

      var cartToggle = event.target.closest('[data-cart-toggle]');
      if (cartToggle && KPWA.cartType === 'drawer' && document.getElementById('CartDrawer')) {
        event.preventDefault();
        Drawers.open('CartDrawer', cartToggle);
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') Drawers.close();
      if (event.key === 'Tab') Drawers.trapFocus(event);
    });
  }

  /* 4. Cart
     Sections that show the cart mark their root with data-cart-section="<section id>".
     After every change Shopify re-renders those sections and we swap them in.
     ------------------------------------------------------------------------ */
  function cartContainers() {
    return qsa('[data-cart-section]');
  }

  function cartSectionIds() {
    return cartContainers().map(function (el) {
      return el.getAttribute('data-cart-section');
    });
  }

  function updateCartCount(count) {
    qsa('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  function renderCartSections(sections) {
    if (!sections) return;
    var parser = new DOMParser();

    cartContainers().forEach(function (live) {
      var id = live.getAttribute('data-cart-section');
      var html = sections[id];
      if (!html) return;
      var fresh = qsa('[data-cart-section]', parser.parseFromString(html, 'text/html')).filter(function (el) {
        return el.getAttribute('data-cart-section') === id;
      })[0];
      if (!fresh) return;
      live.innerHTML = fresh.innerHTML;
      var count = Number(fresh.getAttribute('data-cart-item-count')) || 0;
      live.setAttribute('data-cart-item-count', count);
      updateCartCount(count);
    });
  }

  function setCartLoading(isLoading) {
    cartContainers().forEach(function (el) {
      el.classList.toggle('is-loading', isLoading);
    });
  }

  function showCartError(message) {
    qsa('[data-cart-error]').forEach(function (el) {
      el.textContent = message || strings.cartError || '';
      el.hidden = false;
    });
  }

  function refreshCart() {
    var ids = cartSectionIds();
    if (!ids.length) return Promise.resolve();
    return fetch(window.location.pathname + '?sections=' + encodeURIComponent(ids.join(',')), {
      headers: { Accept: 'application/json' }
    })
      .then(function (response) {
        return response.json();
      })
      .then(renderCartSections)
      .catch(function () {});
  }

  function changeCartLine(line, quantity) {
    setCartLoading(true);
    return fetch(routes.cartChange + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        line: line,
        quantity: quantity,
        sections: cartSectionIds(),
        sections_url: window.location.pathname
      })
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok || data.status) {
            throw new Error(data.description || data.message || strings.cartError);
          }
          renderCartSections(data.sections);
        });
      })
      .catch(function (error) {
        return refreshCart().then(function () {
          showCartError(error.message);
        });
      })
      .then(function () {
        setCartLoading(false);
      });
  }

  function addToCart(form) {
    var button = qs('[data-add-button]', form);
    var label = qs('[data-add-label]', form);
    var errorBox = qs('[data-form-error]', form);
    var originalLabel = label ? label.textContent : '';

    if (button) button.classList.add('is-loading');
    if (label && strings.adding) label.textContent = strings.adding;
    if (errorBox) errorBox.hidden = true;

    var body = new FormData(form);
    body.append('sections', cartSectionIds().join(','));
    body.append('sections_url', window.location.pathname);

    return fetch(routes.cartAdd + '.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: body
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok || data.status) {
            throw new Error(data.description || data.message || strings.cartError);
          }
          renderCartSections(data.sections);
          Drawers.open('CartDrawer', button);
        });
      })
      .catch(function (error) {
        if (errorBox) {
          errorBox.textContent = error.message;
          errorBox.hidden = false;
        }
      })
      .then(function () {
        if (button) button.classList.remove('is-loading');
        if (label) label.textContent = originalLabel;
      });
  }

  function initCart() {
    document.addEventListener('submit', function (event) {
      var form = event.target;
      if (!form.matches || !form.matches('form[data-type="add-to-cart-form"]')) return;
      /* Without a drawer on the page the form posts normally and Shopify opens the cart page. */
      if (KPWA.cartType !== 'drawer' || !document.getElementById('CartDrawer')) return;
      event.preventDefault();
      addToCart(form);
    });

    document.addEventListener('click', function (event) {
      var stepper = event.target.closest('[data-quantity-minus], [data-quantity-plus]');
      if (stepper) {
        var input = qs('input', stepper.closest('[data-quantity]'));
        if (!input) return;
        var min = Number(input.min || 0);
        var current = Number(input.value) || 0;
        var next = stepper.hasAttribute('data-quantity-plus') ? current + 1 : Math.max(min, current - 1);
        if (next !== current) {
          input.value = next;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }

      var remove = event.target.closest('[data-cart-remove]');
      if (remove) {
        event.preventDefault();
        changeCartLine(Number(remove.getAttribute('data-cart-remove')), 0);
      }
    });

    document.addEventListener('change', function (event) {
      var target = event.target;
      if (!target.matches) return;

      if (target.matches('[data-cart-line]')) {
        changeCartLine(Number(target.getAttribute('data-cart-line')), Math.max(0, Number(target.value) || 0));
      } else if (target.matches('[data-cart-note]')) {
        fetch(routes.cartUpdate + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ note: target.value })
        }).catch(function () {});
      }
    });

    /* Keep the Enter key in a quantity box from submitting the cart to checkout. */
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Enter' && event.target.matches && event.target.matches('[data-cart-line]')) {
        event.preventDefault();
        event.target.blur();
      }
    });

    document.addEventListener('submit', function (event) {
      var message = event.target.getAttribute && event.target.getAttribute('data-confirm');
      if (message && !window.confirm(message)) event.preventDefault();
    });
  }

  /* 5. Product variants and gallery
     Each product block is wrapped in [data-product-root] and prints its real
     variants as JSON in <script data-variants>.
     ------------------------------------------------------------------------ */
  function initGallery(gallery) {
    if (gallery.kpwaReady) return;
    gallery.kpwaReady = true;

    var viewport = qs('[data-gallery-viewport]', gallery);
    if (!viewport) return;
    var slides = qsa('.gallery__slide', viewport);
    var thumbs = qsa('[data-gallery-thumb]', gallery);

    function setActive(id) {
      thumbs.forEach(function (thumb) {
        var isActive = thumb.getAttribute('data-gallery-thumb') === String(id);
        thumb.classList.toggle('is-active', isActive);
        if (isActive) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
    }

    function goTo(id, smooth) {
      var index = -1;
      slides.forEach(function (slide, i) {
        if (slide.getAttribute('data-media-id') === String(id)) index = i;
      });
      if (index < 0) return;
      viewport.scrollTo({
        left: index * viewport.clientWidth,
        behavior: smooth && !prefersReducedMotion() ? 'smooth' : 'auto'
      });
      setActive(id);
    }

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        goTo(thumb.getAttribute('data-gallery-thumb'), true);
      });
    });

    var frame;
    viewport.addEventListener(
      'scroll',
      function () {
        window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(function () {
          var slide = slides[Math.round(viewport.scrollLeft / Math.max(1, viewport.clientWidth))];
          if (slide) setActive(slide.getAttribute('data-media-id'));
        });
      },
      { passive: true }
    );

    gallery.kpwaGoTo = goTo;

    var initial = gallery.getAttribute('data-initial-media');
    if (initial) goTo(initial, false);
    else if (slides[0]) setActive(slides[0].getAttribute('data-media-id'));
  }

  /* Slim add-to-cart bar on phones and tablets, shown once the main button has scrolled away. */
  function initStickyBuy(root) {
    var bar = qs('[data-sticky-buy]', root);
    var mainButton = qs('form [data-add-button]', root);
    if (!bar || !mainButton) return;

    var frame;
    function update() {
      var show = mainButton.getBoundingClientRect().bottom < 0;
      bar.classList.toggle('is-visible', show);
      document.body.classList.toggle('has-sticky-buy', show);
    }

    window.addEventListener(
      'scroll',
      function () {
        window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(update);
      },
      { passive: true }
    );
    update();
  }

  function initProduct(root) {
    if (root.kpwaReady) return;
    root.kpwaReady = true;

    qsa('[data-gallery]', root).forEach(initGallery);
    initStickyBuy(root);

    var dataEl = qs('[data-variants]', root);
    var picker = qs('[data-variant-picker]', root);
    if (!dataEl || !picker) return;

    var variants;
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (error) {
      return;
    }

    var idInput = qs('[data-variant-id]', root);
    var groups = qsa('.variant-picker__group', picker);

    function selectedOptions() {
      return groups.map(function (group) {
        var checked = qs('input:checked', group);
        return checked ? checked.value : null;
      });
    }

    function matches(variant, options) {
      return variant.options.every(function (value, index) {
        return value === options[index];
      });
    }

    function markAvailability(options) {
      qsa('.variant-picker__input', picker).forEach(function (input) {
        var position = Number(input.getAttribute('data-option-position')) - 1;
        var candidate = options.slice();
        candidate[position] = input.value;
        var available = variants.some(function (variant) {
          return variant.available && matches(variant, candidate);
        });
        var label = input.nextElementSibling;
        if (label) label.classList.toggle('is-unavailable', !available);
      });
    }

    function showVariant(variant) {
      /* The main button and the mobile buy bar share these attributes. */
      var buttons = qsa('[data-add-button]', root);
      var labels = qsa('[data-add-label]', root);

      if (!variant) {
        buttons.forEach(function (button) {
          button.disabled = true;
        });
        labels.forEach(function (label) {
          label.textContent = strings.unavailable;
        });
        return;
      }

      if (idInput) idInput.value = variant.id;
      buttons.forEach(function (button) {
        button.disabled = !variant.available;
      });
      labels.forEach(function (label) {
        label.textContent = variant.available ? strings.addToCart : strings.soldOut;
      });

      var stickyPrice = qs('[data-sticky-price]', root);
      if (stickyPrice) stickyPrice.innerHTML = formatMoney(variant.price);

      var price = qs('.price--large', root);
      if (price) {
        var onSale = variant.compare_at_price > variant.price;
        var current = qs('[data-price]', price);
        var compare = qs('[data-compare-price]', price);
        var badge = qs('[data-sold-out-badge]', price);
        if (current) current.innerHTML = formatMoney(variant.price);
        if (compare) {
          compare.innerHTML = onSale ? formatMoney(variant.compare_at_price) : '';
          compare.hidden = !onSale;
        }
        if (badge) badge.hidden = variant.available;
        price.classList.toggle('price--sale', onSale);
      }

      var gallery = qs('[data-gallery]', root);
      if (gallery && gallery.kpwaGoTo && variant.featured_media) {
        gallery.kpwaGoTo(variant.featured_media.id, true);
      }

      if (root.getAttribute('data-update-url') === 'true' && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    picker.addEventListener('change', function () {
      var options = selectedOptions();
      options.forEach(function (value, index) {
        var label = qs('[data-option-label="' + (index + 1) + '"]', root);
        if (label) label.textContent = value;
      });
      markAvailability(options);
      showVariant(
        variants.filter(function (variant) {
          return matches(variant, options);
        })[0]
      );
    });

    markAvailability(selectedOptions());
  }

  /* 6. Collection sorting
     ------------------------------------------------------------------------ */
  function initFacets() {
    document.addEventListener('change', function (event) {
      if (event.target.matches && event.target.matches('[data-auto-submit]') && event.target.form) {
        event.target.form.submit();
      }
    });
  }

  /* 7. Reveal on scroll
     ------------------------------------------------------------------------ */
  var revealObserver = null;

  function initReveal(scope) {
    var items = qsa('[data-reveal]', scope);
    if (!items.length) return;

    var inEditor = window.Shopify && window.Shopify.designMode;
    if (inEditor || prefersReducedMotion() || !('IntersectionObserver' in window)) {
      items.forEach(function (item) {
        item.classList.add('is-visible');
      });
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      );
    }

    items.forEach(function (item) {
      revealObserver.observe(item);
    });
  }

  /* 7b. Waitlist
     The form itself is Shopify's customer signup form and posts normally, so Shopify
     saves the customer and can show its own bot check. This script only validates the
     fields, blocks double submissions and scrolls the "Join the waitlist" button.
     ------------------------------------------------------------------------ */
  function initWaitlistForms(scope) {
    qsa('form.waitlist-form', scope).forEach(function (form) {
      if (form.kpwaReady) return;
      form.kpwaReady = true;

      var fields = qs('[data-waitlist-fields]', form);
      if (!fields) return; /* already confirmed: nothing left to submit */

      var email = qs('input[type="email"]', form);
      var consent = qs('[data-waitlist-consent]', form);
      var errorBox = qs('[data-waitlist-error]', form);
      var button = qs('[data-waitlist-submit]', form);
      var label = qs('[data-waitlist-label]', form);
      var idleLabel = label ? label.textContent : '';
      var submitting = false;

      /* Use the theme's own messages instead of the browser's bubbles. */
      form.noValidate = true;

      function fail(message, field) {
        if (errorBox) {
          errorBox.textContent = message;
          errorBox.hidden = false;
        }
        if (field) {
          field.setAttribute('aria-invalid', 'true');
          field.focus();
        }
      }

      function clear() {
        if (errorBox) errorBox.hidden = true;
        [email, consent].forEach(function (field) {
          if (field) field.removeAttribute('aria-invalid');
        });
      }

      form.addEventListener('submit', function (event) {
        clear();

        if (submitting) {
          event.preventDefault();
          return;
        }
        if (!email || !email.value.trim() || !email.checkValidity()) {
          event.preventDefault();
          fail(fields.getAttribute('data-error-email'), email);
          return;
        }
        if (!consent || !consent.checked) {
          event.preventDefault();
          fail(fields.getAttribute('data-error-consent'), consent);
          return;
        }

        submitting = true;
        if (button) {
          button.setAttribute('aria-disabled', 'true');
          button.classList.add('is-loading');
        }
        if (label) label.textContent = fields.getAttribute('data-sending') || idleLabel;
      });

      form.addEventListener('input', clear);

      /* Coming back with the browser's back button: make the form usable again. */
      window.addEventListener('pageshow', function () {
        submitting = false;
        if (button) {
          button.removeAttribute('aria-disabled');
          button.classList.remove('is-loading');
        }
        if (label) label.textContent = idleLabel;
      });
    });
  }

  function initWaitlistLinks() {
    document.addEventListener('click', function (event) {
      var link = event.target.closest('a[href$="#waitlist"]');
      var target = document.getElementById('waitlist');
      if (!link || !target || link.pathname !== window.location.pathname) return;

      event.preventDefault();
      target.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
      if (window.history.replaceState) window.history.replaceState(null, '', '#waitlist');

      /* With a mouse or trackpad, place the cursor in the email field once the scroll settles. */
      var input = qs('input[type="email"]', target);
      if (input && window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        window.setTimeout(function () {
          input.focus({ preventScroll: true });
        }, prefersReducedMotion() ? 0 : 800);
      }
    });
  }

  /* 8. Recommendations and theme editor
     ------------------------------------------------------------------------ */
  function initRecommendations(scope) {
    qsa('[data-recommendations]', scope).forEach(function (section) {
      var url = section.getAttribute('data-url');
      if (!url || section.kpwaReady || section.children.length) return;
      section.kpwaReady = true;

      fetch(url)
        .then(function (response) {
          return response.text();
        })
        .then(function (html) {
          var fresh = qs('[data-recommendations]', new DOMParser().parseFromString(html, 'text/html'));
          if (fresh && fresh.innerHTML.trim()) {
            section.innerHTML = fresh.innerHTML;
            initReveal(section);
          }
        })
        .catch(function () {});
    });
  }

  function initScope(scope) {
    qsa('[data-product-root]', scope).forEach(initProduct);
    qsa('[data-gallery]', scope).forEach(initGallery);
    initReveal(scope);
    initRecommendations(scope);
    initWaitlistForms(scope);
  }

  function start() {
    initHeader();
    initDrawers();
    initCart();
    initFacets();
    initWaitlistLinks();
    initScope(document);

    document.addEventListener('shopify:section:load', function (event) {
      initScope(event.target);
      if (qs('[data-header]', event.target)) initHeader();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
